import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { titleCaseName } from "@/lib/utils";
import { PhoneInput } from "@/components/PhoneInput";
import { COUNTRIES } from "@/lib/countries";
import { toast } from "sonner";
import { Loader2, LogOut, Shield, User as UserIcon, AlertCircle, Camera, KeyRound, Flame, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

const LANGS = [
  { code: "fr", label: "Français" }, { code: "en", label: "English" },
  { code: "es", label: "Español" }, { code: "de", label: "Deutsch" }, { code: "it", label: "Italiano" },
];

export default function Profil() {
  const { user, loading, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [language, setLanguage] = useState("fr");
  const [notif, setNotif] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingPwd, setEditingPwd] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);
  const [livingEnabled, setLivingEnabled] = useState(false);
  const avatarRef = useRef();

  useEffect(() => {
    if (!loading && !user) { navigate("/"); return; }
    if (user) {
      setFirst(user.first_name || "");
      setLast(user.last_name || "");
      setAvatar(user.avatar_base64 || null);
      setPhone(user.phone || "");
      setCountry(user.country || "");
      setLanguage(user.language || "fr");
      setNotif(user.notifications_enabled !== false);
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    api.get("/living-candle/access").then((r) => setLivingEnabled(!!r.data?.enabled)).catch(() => {});
  }, []);


  const onAvatarFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Photo trop lourde (max 3 Mo)");
    const reader = new FileReader();
    reader.onload = () => setAvatar(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch("/me/profile", {
        first_name: first, last_name: last, avatar_base64: avatar ?? "", phone, country, language, notifications_enabled: notif,
      });
      setUser(res.data);
      toast.success(t("profile.saved"));
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("profile.error"));
    } finally { setSaving(false); }
  };

  const cancelEdit = () => {
    if (user) {
      setFirst(user.first_name || "");
      setLast(user.last_name || "");
      setAvatar(user.avatar_base64 || null);
      setPhone(user.phone || "");
      setCountry(user.country || "");
      setLanguage(user.language || "fr");
      setNotif(user.notifications_enabled !== false);
    }
    setEditing(false);
  };

  const changePassword = async () => {
    if (newPwd.length < 6) return toast.error(t("profile.pwdTooShort"));
    if (newPwd !== newPwd2) return toast.error(t("profile.pwdMismatch"));
    setChangingPwd(true);
    try {
      await api.post("/me/password", { current_password: curPwd || null, new_password: newPwd });
      toast.success(t("profile.pwdChanged"));
      setCurPwd(""); setNewPwd(""); setNewPwd2("");
      setEditingPwd(false);
      if (user) setUser({ ...user, has_password: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("profile.error"));
    } finally { setChangingPwd(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-[100dvh]">
      <Header />
      <div className="mx-auto max-w-md px-5 py-8 pb-40">
        <div className="mb-6 flex items-center gap-3">
          <button type="button" onClick={() => avatarRef.current?.click()} className="group relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-amber-300/80 bg-card/60 text-amber-200 shadow-[0_0_18px_rgba(251,191,36,0.3)]" data-testid="profile-avatar-btn">
            {avatar ? <img src={avatar} alt="avatar" className="h-full w-full object-cover" data-testid="profile-avatar-img" /> : <UserIcon size={26} />}
            <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"><Camera size={18} className="text-white" /></span>
          </button>
          <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={onAvatarFile} data-testid="profile-avatar-input" />
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-medium">
              {`${first} ${last}`.trim() || t("profile.title")}
              {(() => { const c = COUNTRIES.find((x) => x.code === country); return c ? <span className="text-xl" title={c.name} data-testid="profile-country-flag">{c.flag}</span> : null; })()}
            </h1>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>

        <div className="space-y-4 rounded-3xl border border-white/10 bg-card/40 p-6">
          {(!first || !last || !phone || !country) && (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-200" data-testid="profile-incomplete-banner">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <span>{t("profile.missingIntro")} {[!first && t("auth.firstName"), !last && t("auth.lastName"), !phone && t("profile.phone"), !country && t("profile.country")].filter(Boolean).join(", ")}.</span>
            </div>
          )}
          {!editing ? (
            <div className="space-y-3" data-testid="profile-info-view">
              <div className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-sm text-muted-foreground">{t("auth.firstName")} & {t("auth.lastName")}</span>
                <span className="text-right text-sm font-medium">{`${first} ${last}`.trim() || "—"}</span>
              </div>
              <div className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-sm text-muted-foreground">{t("profile.email")}</span>
                <span className="truncate text-right text-sm font-medium">{user.email}</span>
              </div>
              <div className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-sm text-muted-foreground">{t("profile.phone")}</span>
                <span className="text-right text-sm font-medium">{phone || "—"}</span>
              </div>
              <div className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-sm text-muted-foreground">{t("profile.country")}</span>
                <span className="text-right text-sm font-medium">{(() => { const c = COUNTRIES.find((x) => x.code === country); return c ? `${c.flag} ${c.name}` : "—"; })()}</span>
              </div>
              <div className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-sm text-muted-foreground">{t("profile.language")}</span>
                <span className="text-right text-sm font-medium">{LANGS.find((l) => l.code === language)?.label || language}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">{t("profile.notifications")}</span>
                <span className="text-right text-sm font-medium">{notif ? "Activées" : "Désactivées"}</span>
              </div>
              <Button className="mt-2 w-full rounded-full" onClick={() => setEditing(true)} data-testid="profile-edit-btn">
                <Pencil size={16} className="mr-2" /> Modifier mes informations
              </Button>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("auth.firstName")}</Label>
              <Input value={first} onChange={(e) => setFirst(titleCaseName(e.target.value))} data-testid="profile-first-name-input" />
            </div>
            <div>
              <Label>{t("auth.lastName")}</Label>
              <Input value={last} onChange={(e) => setLast(titleCaseName(e.target.value))} data-testid="profile-last-name-input" />
            </div>
          </div>
          <div>
            <Label>{t("profile.email")}</Label>
            <Input value={user.email} disabled className="opacity-60" data-testid="profile-email-input" />
          </div>
          <div>
            <Label>{t("profile.phone")}</Label>
            <PhoneInput code={country || "FR"} phone={phone} onChange={({ code, phone: full }) => { setCountry(code); setPhone(full); }} testid="profile-phone" />
          </div>
          <div>
            <Label>{t("profile.language")}</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="profile-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-2xl border border-white/10 px-4 py-3">
            <span className="text-sm">{t("profile.notifications")}</span>
            <Switch checked={notif} onCheckedChange={setNotif} data-testid="profile-notifications-switch" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-full border-white/15" onClick={cancelEdit} disabled={saving} data-testid="profile-cancel-btn">
              Annuler
            </Button>
            <Button className="flex-1 rounded-full" onClick={save} disabled={saving} data-testid="profile-save-btn">
              {saving ? <Loader2 className="animate-spin" size={16} /> : t("profile.save")}
            </Button>
          </div>
          </>
          )}
        </div>

        <div className="mt-4 space-y-4 rounded-3xl border border-white/10 bg-card/40 p-6" data-testid="profile-password-card">
          <div className="flex items-center gap-2 text-sm font-medium"><KeyRound size={16} className="text-sky-400" /> {t("profile.pwdSection")}</div>
          {!editingPwd ? (
            <div className="flex items-center justify-between gap-3" data-testid="profile-password-view">
              <span className="text-sm text-muted-foreground">{t("profile.newPassword").replace(/nouveau /i, "").replace(/new /i, "") || "Mot de passe"}</span>
              <span className="text-right text-lg tracking-widest text-muted-foreground">••••••••</span>
            </div>
          ) : (
          <>
          {user.has_password !== false && (
            <div>
              <Label>{t("profile.currentPassword")}</Label>
              <PasswordInput value={curPwd} onChange={(e) => setCurPwd(e.target.value)} data-testid="profile-current-password-input" />
            </div>
          )}
          <div>
            <Label>{t("profile.newPassword")}</Label>
            <PasswordInput value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="profile-new-password-input" />
          </div>
          <div>
            <Label>{t("profile.confirmPassword")}</Label>
            <PasswordInput value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)} data-testid="profile-confirm-password-input" />
          </div>
          </>
          )}
          {!editingPwd ? (
            <Button variant="outline" className="w-full rounded-full border-white/15" onClick={() => setEditingPwd(true)} data-testid="profile-edit-password-btn">
              <Pencil size={16} className="mr-2" /> {t("profile.changePassword")}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 rounded-full border-white/15" onClick={() => { setCurPwd(""); setNewPwd(""); setNewPwd2(""); setEditingPwd(false); }} disabled={changingPwd} data-testid="profile-cancel-password-btn">
                Annuler
              </Button>
              <Button className="flex-1 rounded-full" onClick={changePassword} disabled={changingPwd || !newPwd} data-testid="profile-change-password-btn">
                {changingPwd ? <Loader2 className="animate-spin" size={16} /> : t("profile.changePassword")}
              </Button>
            </div>
          )}
        </div>

        <div className="mt-4 space-y-2">
          {(livingEnabled || user.role === "admin") && (
            <Button onClick={() => navigate("/ma-bougie")} data-testid="profile-living-candle-btn"
              className="h-14 w-full rounded-full border-0 text-base font-semibold text-white shadow-lg transition-all hover:brightness-110"
              style={{ background: `linear-gradient(180deg, rgba(14,99,201,0.72) 0%, rgba(47,143,214,0.6) 60%, rgba(76,159,224,0.55) 100%), url(${process.env.PUBLIC_URL}/living-sky.jpg) center 25% / cover`, textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>
              <Flame size={19} className="mr-2 drop-shadow" /> Ma bougie de mon vivant
            </Button>
          )}
          {user.role === "admin" && (
            <Button variant="outline" className="w-full rounded-full border-white/15 text-sky-400" onClick={() => navigate("/admin")} data-testid="profile-admin-btn">
              <Shield size={16} className="mr-2" /> {t("header.admin")}
            </Button>
          )}
          <Button variant="ghost" className="w-full rounded-full text-muted-foreground" onClick={() => { logout(); navigate("/"); }} data-testid="profile-logout-btn">
            <LogOut size={16} className="mr-2" /> {t("profile.logout")}
          </Button>
        </div>
      </div>
    </div>
  );
}
